import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useSessionState } from './SessionContext';
import {
  createService as createServiceRequest,
  deleteService as deleteServiceRequest,
  fetchServices,
  updateService as updateServiceRequest,
} from '@/lib/api';
import type { ServiceCatalogEntry, ServiceDraft, ServiceUpdate } from '@/lib/types';

type ServiceCatalogContextValue = {
  isReady: boolean;
  error: string | null;
  services: ServiceCatalogEntry[];
  addService: (draft: ServiceDraft) => Promise<ServiceCatalogEntry>;
  updateService: (
    serviceId: ServiceCatalogEntry['id'],
    updates: ServiceUpdate
  ) => Promise<ServiceCatalogEntry>;
  deleteService: (serviceId: ServiceCatalogEntry['id']) => Promise<boolean>;
  refreshServices: () => Promise<void>;
};

const ServiceCatalogContext = createContext<ServiceCatalogContextValue | null>(null);

export function ServiceCatalogProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useSessionState();
  const [services, setServices] = useState<ServiceCatalogEntry[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetServices = useCallback(() => {
    setServices([]);
    setError(null);
  }, []);

  const refreshServices = useCallback(async () => {
    if (!isAuthenticated) {
      resetServices();
      return;
    }

    try {
      const items = await fetchServices();
      setServices(items);
      setError(null);
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : 'Unable to load services.');
    }
  }, [isAuthenticated, resetServices]);

  useEffect(() => {
    let active = true;

    (async () => {
      if (!isAuthenticated) {
        resetServices();

        if (active) {
          setIsReady(true);
        }

        return;
      }

      await refreshServices();

      if (active) {
        setIsReady(true);
      }
    })();

    return () => {
      active = false;
    };
  }, [isAuthenticated, refreshServices, resetServices]);

  const addService = useCallback(async (draft: ServiceDraft) => {
    const createdService = await createServiceRequest(draft);
    setServices((current) => [createdService, ...current.filter((service) => service.id !== createdService.id)]);
    setError(null);

    return createdService;
  }, []);

  const updateService = useCallback(async (serviceId: ServiceCatalogEntry['id'], updates: ServiceUpdate) => {
    const updatedService = await updateServiceRequest(serviceId, updates);

    setServices((current) =>
      current.map((service) => (service.id === serviceId ? updatedService : service))
    );
    setError(null);

    return updatedService;
  }, []);

  const deleteService = useCallback(async (serviceId: ServiceCatalogEntry['id']) => {
    const removed = await deleteServiceRequest(serviceId);

    if (removed.deleted) {
      setServices((current) => current.filter((service) => service.id !== serviceId));
      setError(null);
    }

    return Boolean(removed.deleted);
  }, []);

  const value = useMemo(
    () => ({
      isReady,
      error,
      services,
      addService,
      updateService,
      deleteService,
      refreshServices,
    }),
    [addService, deleteService, error, isReady, refreshServices, services, updateService]
  );

  return <ServiceCatalogContext.Provider value={value}>{children}</ServiceCatalogContext.Provider>;
}

export function useServiceCatalogState() {
  const context = useContext(ServiceCatalogContext);

  if (!context) {
    throw new Error('useServiceCatalogState must be used within a ServiceCatalogProvider');
  }

  return context;
}

export type { ServiceDraft, ServiceUpdate };
